import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import Axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import {
  Badge,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Popover,
  Typography,
} from "@mui/material";
import DOMPurify from "dompurify";
import { format, parseISO } from "date-fns";
import { useSnackbar } from "notistack";
import { useLocation } from "react-router-dom";

import { axios } from "../api";
import { useGlobalConfig } from "../hooks/useGlobalConfig";
import {
  topBarIconButtonSx,
  topBarIconSx,
} from "../components/dropdownMenuStyles";

interface NotificationAttachment {
  name: string;
  url: string;
}

interface FollowupNotification {
  recipient_id: number;
  followup_id: number;
  message: string;
  content: string;
  from_name: string;
  created_at: string | null;
  source: string;
  source_id: number;
  status: number;
  read: boolean;
  attachments: NotificationAttachment[];
}

interface NotificationCountResponse {
  count: number;
}

const unreadStatus = 2;

const NotificationsMenu: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const globalConfig = useGlobalConfig();
  const location = useLocation();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const autoOpenedRef = useRef(false);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [count, setCount] = useState(0);
  const [notifications, setNotifications] = useState<FollowupNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<FollowupNotification | null>(null);

  const open = Boolean(anchorEl);

  const formatNotificationDate = useCallback(
    (value: string | null) => {
      if (!value) {
        return "";
      }

      try {
        const parsed = parseISO(value);
        return format(parsed, globalConfig?.date_format || "dd-MMM-yyyy HH:mm:ss");
      } catch (_error) {
        return value;
      }
    },
    [globalConfig]
  );

  const loadCount = useCallback(async () => {
    try {
      const response = await axios.get<NotificationCountResponse>("/v6/followups/notifications/count", {
        id: "followup-notification-count",
        cache: false,
      });
      setCount(response.data.count || 0);
    } catch (error: any) {
      const isLocal = process.env.REACT_APP_ENV === "local" || window.location.hostname.includes("localhost") || window.location.hostname.includes("127.0.0.1");
      if (!Axios.isCancel(error) && error?.response?.status !== 401 && error?.response?.status !== 404 && !isLocal) {
        enqueueSnackbar("Error loading notifications", {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        });
      }
    }
  }, [enqueueSnackbar]);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const [notificationResponse, countResponse] = await Promise.all([
        axios.get<FollowupNotification[]>("/v6/followups/notifications", {
          id: "followup-notifications",
          cache: false,
        }),
        axios.get<NotificationCountResponse>("/v6/followups/notifications/count", {
          id: "followup-notification-count-open",
          cache: false,
        }),
      ]);

      setNotifications(notificationResponse.data || []);
      setCount(countResponse.data.count || 0);
    } catch (error: any) {
      const isLocal = process.env.REACT_APP_ENV === "local" || window.location.hostname.includes("localhost") || window.location.hostname.includes("127.0.0.1");
      if (!Axios.isCancel(error) && error?.response?.status !== 401 && error?.response?.status !== 404 && !isLocal) {
        enqueueSnackbar("Error loading notifications", {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        });
      }
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const markAsRead = useCallback(async (recipientId: number) => {
    await axios.post(`/v6/followups/notifications/${recipientId}/read`, undefined, {
      cache: false,
    });

    setNotifications((currentNotifications) =>
      currentNotifications.map((notification) =>
        notification.recipient_id === recipientId
          ? { ...notification, read: true, status: 1 }
          : notification
      )
    );
    setSelectedNotification((currentNotification) =>
      currentNotification?.recipient_id === recipientId
        ? { ...currentNotification, read: true, status: 1 }
        : currentNotification
    );
  }, []);

  const archiveNotification = useCallback(
    async (recipientId: number) => {
      try {
        await axios.post(`/v6/followups/notifications/${recipientId}/archive`, undefined, {
          cache: false,
        });

        setNotifications((currentNotifications) =>
          currentNotifications.filter((notification) => notification.recipient_id !== recipientId)
        );
        setSelectedNotification((currentNotification) =>
          currentNotification?.recipient_id === recipientId ? null : currentNotification
        );
        setCount((currentCount) => Math.max(currentCount - 1, 0));
      } catch (error) {
        if (!Axios.isCancel(error)) {
          enqueueSnackbar("Error archiving notification", {
            variant: "error",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "center",
            },
          });
        }
      }
    },
    [enqueueSnackbar]
  );

  const handleOpen = useCallback(
    async (target: HTMLElement) => {
      setAnchorEl(target);
      setSelectedNotification(null);
      await loadNotifications();
    },
    [loadNotifications]
  );

  const handleToggle = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      if (open) {
        setAnchorEl(null);
        setSelectedNotification(null);
        return;
      }

      await handleOpen(event.currentTarget);
    },
    [handleOpen, open]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedNotification(null);
  }, []);

  const handleSelectNotification = useCallback(
    async (notification: FollowupNotification) => {
      setSelectedNotification(notification);

      if (notification.status === unreadStatus) {
        try {
          await markAsRead(notification.recipient_id);
        } catch (error) {
          if (!Axios.isCancel(error)) {
            enqueueSnackbar("Error marking notification as read", {
              variant: "error",
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "center",
              },
            });
          }
        }
      }
    },
    [enqueueSnackbar, markAsRead]
  );

  const sanitizedContent = useMemo(
    () => ({
      __html: DOMPurify.sanitize(selectedNotification?.content || "", {
        ADD_ATTR: ["target", "rel"],
      }),
    }),
    [selectedNotification]
  );

  useEffect(() => {
    loadCount();
  }, [loadCount]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("open_followups") !== "true") {
      return;
    }

    if (autoOpenedRef.current || !buttonRef.current) {
      return;
    }

    autoOpenedRef.current = true;
    handleOpen(buttonRef.current);
  }, [handleOpen, location.search]);

  return (
    <>
      <IconButton
        ref={buttonRef}
        aria-label="notifications"
        color="inherit"
        onClick={handleToggle}
        sx={topBarIconButtonSx}
      >
        <Badge
          badgeContent={count}
          color="error"
          invisible={count === 0}
          sx={{
            "& .MuiBadge-badge": {
              fontSize: 10,
              fontWeight: 700,
              minWidth: 16,
              height: 16,
            },
          }}
        >
          <NotificationsOutlinedIcon sx={topBarIconSx} />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            width: { xs: "calc(100vw - 32px)", sm: 640 },
            maxWidth: "calc(100vw - 32px)",
            borderRadius: 0,
            boxShadow: "0 8px 28px rgba(0, 0, 0, 0.18)",
            p: { xs: 2.5, sm: 4 },
          },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: "#008e92",
            fontSize: { xs: 36, sm: 42 },
            fontWeight: 500,
            lineHeight: 1.1,
            mb: 3,
          }}
        >
          Notifications
        </Typography>

        <Box sx={{ display: "flex", gap: 3, borderBottom: "2px solid #e0e0e0", mb: 2.5 }}>
          <Typography
            sx={{
              color: "#008e92",
              fontWeight: 700,
              fontSize: 14,
              pb: 1.25,
              borderBottom: "3px solid #008e92",
            }}
          >
            Follow Ups
          </Typography>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress size={32} />
          </Box>
        ) : selectedNotification ? (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mb: 2,
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => setSelectedNotification(null)}
                sx={{
                  border: 0,
                  background: "transparent",
                  color: "#008e92",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  p: 0,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                <ArrowBackIcon fontSize="small" />
                Back
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, color: "#666" }}>
                <Typography sx={{ fontSize: 14 }}>
                  {formatNotificationDate(selectedNotification.created_at)}
                </Typography>
                <IconButton
                  aria-label="Archive notification"
                  size="small"
                  onClick={() => archiveNotification(selectedNotification.recipient_id)}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            <Divider />

            <Typography
              sx={{
                fontSize: 14,
                color: "#333",
                lineHeight: 1.7,
                py: 3,
                "& a": {
                  color: "#1976d2",
                },
              }}
              dangerouslySetInnerHTML={sanitizedContent}
            />

            {selectedNotification.attachments.length > 0 && (
              <Box sx={{ pt: 2.5, borderTop: "1px solid #e0e0e0" }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.25 }}>
                  Attachments:
                </Typography>
                {selectedNotification.attachments.map((attachment) => (
                  <Link
                    key={attachment.url}
                    href={attachment.url}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    sx={{ display: "block", color: "#008e92", fontSize: 14, mb: 0.5 }}
                  >
                    {attachment.name}
                  </Link>
                ))}
              </Box>
            )}
          </Box>
        ) : notifications.length === 0 ? (
          <Box py={6} textAlign="center">
            <Typography sx={{ color: "#777", fontSize: 14 }}>No follow ups</Typography>
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 2.3fr) minmax(120px, 1.2fr) minmax(160px, 1.3fr) 32px",
                gap: 2,
                px: 1,
                pb: 1,
                color: "#222",
                fontSize: 13,
                fontWeight: 700,
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Box>Message</Box>
              <Box>From</Box>
              <Box>Date</Box>
              <Box />
            </Box>

            {notifications.map((notification) => (
              <Box
                key={notification.recipient_id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 2.3fr) minmax(120px, 1.2fr) minmax(160px, 1.3fr) 32px",
                  gap: 2,
                  alignItems: "center",
                  px: 1,
                  py: 1.5,
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => handleSelectNotification(notification)}
                  sx={{
                    border: 0,
                    background: "transparent",
                    textAlign: "left",
                    p: 0,
                    cursor: "pointer",
                    color: "#333",
                    fontSize: 14,
                    fontWeight: notification.read ? 400 : 700,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {notification.message || "Untitled notification"}
                </Box>
                <Typography sx={{ fontSize: 14, color: "#333" }}>
                  {notification.from_name || "System"}
                </Typography>
                <Typography sx={{ fontSize: 14, color: "#333" }}>
                  {formatNotificationDate(notification.created_at)}
                </Typography>
                <IconButton
                  aria-label={`Archive ${notification.message || "notification"}`}
                  size="small"
                  onClick={() => archiveNotification(notification.recipient_id)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Popover>
    </>
  );
};

export default NotificationsMenu;